import isFunction from 'lodash/isFunction';
import get from 'lodash/get';
import pick from 'lodash/pick';
import {
    USER_ROLES } from '@/constants';
import { LOAD_STATUS } from '@/stores/constants/load-status';
import { useOrganizationStore } from '@/stores/organization.store';
import { useAppsStore } from '@/stores/apps.store';
import { useVocItemsStore } from '@/stores/voc-items.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import {
    getTrialData,
} from '@/stores/helpers/subscription.helper';
import { getSanitizedUrlParam } from '@/utils/url';
import { getPendoApiKey, setupPendoAnalyticsSnippet } from '@/utils/analytics';
import { BETA_OPT_INS_SETTING, toBetaOptIns } from '@pendo/middleware';
import { getPermissionString } from './permissions.util';
import { $canOrg } from '@pendo/permissions';
import { isProdEnvironment } from '@/settings/environment.utils.js';
import { setTag as sentrySetTag } from '@sentry/vue';

import { useSubscriptionStore } from '@/stores/subscription.store';
import { useProductLinesStore } from '@/stores/product-lines.store';

import { useAuthStore } from '@/stores/auth.store';

function subCookieIdentifierMap () {
    const isProd = isProdEnvironment();
    const isProdIO = window.appEnvId === 'prod';
    const isLocal = __PROCESS__.LOCAL_ENV;
    let cookieDomain = '.pendo-dev.com';

    if (isProdIO) {
        cookieDomain = '.pendo.io';
    } else if (isProd || isLocal) {
        return {};
    }

    return {
        cookieDomain,
    };
}

const networkLogs = {
    excludeRequestUrls: [
        /https:\/\/firestore\.googleapis\.com\/.*/,
        /.*password.*/,
        /.*login.*/,
        /.*signup.*/,
        /.*token.*/,
    ],
    captureRequestBody: (body) => body,
    captureResponseBody: (body) => body,
};
const pendoAnalyticsAllowedEnvironments = ['prod', 'test', 'dev', 'atlas', 'magic'];

const REPORT_ID_URL_REGEXES = [
    /^https?:\/\/[^/]*pendo[^/]*\/s\/[^/#?;]+\/(?:data-explorer|path|funnel|retention|workflow)\/([^/#?;]+)\/?(?:;[^#]*)?(?:\?[^#]*)?(?:#.*)?$/,
    /^https?:\/\/[^/]*pendo[^/]*\/s\/[^/#?;]+\/workflow\/([^/#?;]+)\/(?:journeys|home)\/?(?:;[^#]*)?(?:\?[^#]*)?(?:#.*)?$/,
    /^https?:\/\/[^/]*pendo[^/]*\/s\/[^/#?;]+\/(?:visitors|accounts)\/reports\/([^/#?;]+)\/?(?:;[^#]*)?(?:\?[^#]*)?(?:#.*)?$/,
];

// Exclude static /pages children (untagged, alerts) and allow the
// PageParameterDetails suffix (/parameters/<parameterId>) so drilling into
// a page parameter still credits its parent pageId.
const PAGE_ID_URL_REGEXES = [
    /^https?:\/\/[^/]*pendo[^/]*\/s\/[^/#?;]+\/pages\/(?!(?:untagged|alerts)(?:[/?;#]|$))([^/#?;]+)(?:\/parameters\/[^/#?;]+)?\/?(?:;[^#]*)?(?:\?[^#]*)?(?:#.*)?$/,
];

function toAbsoluteHref (url, base) {
    if (typeof url !== 'string' || !url) {
        return null;
    }

    try {
        return new URL(url, base).href;
    } catch {
        return null;
    }
}

function matchFirst (url, regexes) {
    if (!url) {
        return null;
    }

    for (const regex of regexes) {
        try {
            const match = url.match(regex);
            if (match) {
                return match[1];
            }
        } catch {
            // swallow so a single failing rule can't block the rest
        }
    }

    return null;
}

function matchReportId (url) {
    return matchFirst(url, REPORT_ID_URL_REGEXES);
}

function matchPageId (url) {
    return matchFirst(url, PAGE_ID_URL_REGEXES);
}

// Only trust the target's href on click events — a focus / hover / change
// on a link shouldn't credit that link's URL to the captured event. Walks
// up to the nearest anchor so clicks on nested nodes (icons, spans inside
// pendo-router-link) still surface the link's href.
function getTargetHrefForClick (eventInfo) {
    if (eventInfo?.type !== 'click') {
        return null;
    }

    const anchor = eventInfo.target?.closest?.('a[href]') ?? null;

    return anchor?.getAttribute?.('href') ?? null;
}

// Extract the reportId / pageId to stamp onto a captured event. Current-URL
// matches always win, so target-href resolution is deferred until the
// location branches fail. pageId is location-only; target href (click events
// only) can contribute a reportId. Target hrefs are resolved against the
// current location so router-link relative paths still match.
export function getCapturedIdsForEvent (eventInfo) {
    const location = toAbsoluteHref(window.location?.href);

    const locationReportId = matchReportId(location);
    if (locationReportId) {
        return { key: 'reportId', value: locationReportId, source: 'windowLocation' };
    }

    const locationPageId = matchPageId(location);
    if (locationPageId) {
        return { key: 'pageId', value: locationPageId, source: 'windowLocation' };
    }

    const target = toAbsoluteHref(getTargetHrefForClick(eventInfo), location || undefined);
    const targetReportId = matchReportId(target);
    if (targetReportId) {
        return { key: 'reportId', value: targetReportId, source: 'targetHref' };
    }

    return null;
}

export function applyCapturedIdsToEvent (eventInfo) {
    try {
        if (!eventInfo || !isFunction(eventInfo.addEventProperty)) {
            return;
        }

        const result = getCapturedIdsForEvent(eventInfo);
        if (!result) {
            return;
        }

        eventInfo.addEventProperty(result.key, result.value);
        eventInfo.addEventProperty(`${result.key}Source`, result.source);
    } catch {
        // never let a capture error break the agent
    }
}

export async function identify () {
    const { visitor, account } = await formatMetadata();
    const subscription = useSubscriptionStore().active;

    const allowPartnerAnalyticsForwarding = pendoAnalyticsAllowedEnvironments.includes(__PROCESS__.PENDO_ENV);

    let disableGuides = false;
    try {
        // TEMP TO RESOLVE SEV1
        const instructureSubIds = [
            '4820939051958272', // io sub id
            '5774894566670336', // eu sub id
            '6214176238403584', // au sub id
            '6282400585940992', // jpn sub id

            '4656217648267264', // io - instructure bridge
            '6742282203889664', // io - instructure sandbox
            '4820939051958272', // io - instructure7
            '6750799550152704', // io - instructure10

            '5774894566670336', // eu - instructure oem

            '6214176238403584', // au - instructure oem

            '6282400585940992', // jpn - instructure oem
        ];

        const subName = subscription.displayName || '';
        const isInstructureRelatedAEU = instructureSubIds.includes(String(account?.partnerSubId));
        const isInstructureRelatedSub = instructureSubIds.includes(String(subscription.id));
        const isInstructureRelatedSubNameCheck = subName?.toLowerCase()?.includes('instructure');
        const isInstructureHostname = window.location.hostname.includes('instructure');
        disableGuides = isInstructureRelatedAEU || isInstructureRelatedSubNameCheck || isInstructureRelatedSub || isInstructureHostname;
    } catch(err) {
        console.error(err);
    }

    if (!useAuthStore().initialized) {
        window.pendo.initialize({
            guides: { disabled: disableGuides },
            events: {
                deliverablesLoaded () {
                    const segmentFlags = window.pendo.segmentFlags || [];
                    useAuthStore().setUserSegmentFlags({ flags: segmentFlags });
                    useAuthStore().setSegmentFlagsLoadStatus({ status: LOAD_STATUS.COMPLETE });
                },
                guidesFailed () {
                    const segmentFlags = window.pendo.segmentFlags || [];
                    useAuthStore().setUserSegmentFlags({ flags: segmentFlags });
                    useAuthStore().setSegmentFlagsLoadStatus({ status: LOAD_STATUS.COMPLETE });
                },
                onEventCaptured (eventInfo) {
                    applyCapturedIdsToEvent(eventInfo);
                },
            },
            visitor,
            account,
            allowPartnerAnalyticsForwarding,
            apiKey: getPendoApiKey(),
            ...subCookieIdentifierMap(),
            networkLogs,
        });
        useAuthStore().setInitialized({ initialized: true });
    } else {
        if (window.pendo && isFunction(window.pendo.identify)) {
            window.pendo.identify({ visitor, account });
        }

        if (window.FS && isFunction(window.FS.identify)) {
            window.FS.identify(visitor.id, pick(visitor, ['email', 'firstName', 'lastName', 'role']));
        }
    }

    if (subscription?.name) {
        try {
            // does not use account metadata because account.id is always set to `pendo-internal`
            // for super users, which would miscategorize perf tracing
            sentrySetTag('account', subscription.name);
        } catch(err) {
            console.error(err);
        }
    }

    const isOem = !!subscription.trainingPartnerSubscriptionId;
    if (!allowPartnerAnalyticsForwarding) {
        const { apiKey: psaApiKey, snippetLocation: psaSnippetLocation } = subscription.pendoSubAnalytics || {};
        const { partnerApiKey, partnerSnippetLocation, accountId: oemAccountId } = subscription.trainingAttributes || {};

        let apiKey = '';
        let snippetLocation = '';
        const pendoAnalyticsAccount = account;
        if (psaApiKey && psaSnippetLocation) {
            apiKey = psaApiKey;
            snippetLocation = psaSnippetLocation;
        } else if (isOem) {
            apiKey = partnerApiKey;
            snippetLocation = partnerSnippetLocation;
            pendoAnalyticsAccount.id = oemAccountId;
        }
        if (apiKey && snippetLocation && pendoAnalyticsAccount.id) {
            setupPendoAnalyticsSnippet({ apiKey, snippetLocation });
            window.pendoPartner?.identify({ visitor, account: pendoAnalyticsAccount });
        }
    }

    // write a shared cookie for help center auth using identify info
    // this probably belongs somewhere else...
    const numDays = 100;
    const duration = new Date();
    duration.setDate(duration.getDate() + numDays);
    sendMarketingPageRegistrationReferralEvent(account, visitor);
    const value = {
        visitorId: visitor.id,
        accountId: account.id,
    };
    const cookieValue = encodeURIComponent(JSON.stringify(value));
    document.cookie = `pendo_identity=${cookieValue}; expires=${duration.toUTCString()}; secure=true; domain=.pendo.io; path=/`;
}

export async function identifyAnonymous () {
    pendo.initialize({
        apiKey: getPendoApiKey(),
        ...subCookieIdentifierMap(),
        networkLogs,
    });
}

export async function formatMetadata () {
    const subscription = useSubscriptionStore().active;
    const applications = useAppsStore().list;

    // When switching subscriptions
    // product lines don't get hydrated in time to be included in the metadata
    await useProductLinesStore().loadAll();

    await Promise.allSettled([
        useUserSettingsStore().loadOne({ name: BETA_OPT_INS_SETTING, namespaced: true }),
        useSubscriptionStore().loadSetting({ setting: BETA_OPT_INS_SETTING, retrieveMetadata: false }),
    ]);

    const visitor = formatVisitorMetadata({ user: useAuthStore().user, subscription });
    const account = formatAccountMetadata({
        subscription,
        applications,
        productLines: useProductLinesStore().map,
    });

    return {
        visitor,
        account,
    };
}

function formatVisitorDiscoverPermission () {
    const discoverManager = useVocItemsStore().canEditVocItems;
    const { canCreateVocItems } = useVocItemsStore();
    const { isAdmin } = useAuthStore();

    if (isAdmin) {
        return 'Admin';
    }
    if (discoverManager) {
        return 'Manager';
    }
    if (canCreateVocItems) {
        return 'Contributor';
    }

    return 'Viewer';
}

function formatVisitorOrgRole () {
    const orgAdmin =
        $canOrg()
            .entity('organizationSetting')
            .action('view') &&
        $canOrg()
            .entity('organizationSetting')
            .action('edit');
    const orgContentManager = $canOrg()
        .entity('organizationResources')
        .action('edit');
    const orgReporter =
        $canOrg()
            .entity('organizationMau')
            .action('view') &&
        $canOrg()
            .entity('organizationSummary')
            .action('view');

    if (orgAdmin) {
        return 'Admin';
    }
    const roles = [];
    if (orgContentManager) {
        roles.push('Content Manager');
    }
    if (orgReporter) {
        roles.push('Reporter');
    }

    return roles.join(', ');
}

function formatVisitorMetadata ({ user, subscription }) {
    const isSuperUser = useAuthStore().isSuper;
    const username = user.username && user.username.toLowerCase();
    const userFlags = useAuthStore().flagList;
    const discoverPermission = formatVisitorDiscoverPermission();
    const discoverGetStartedPreferences = useUserSettingsStore().settingByName(
        'discoverGetStartedPreferences',
        true,
    );
    const { isMultiApp } = useAppsStore();
    const organization = useOrganizationStore().getActive;
    const windowScreenSizeMetadata = `${window.screen.width}x${window.screen.height}`;
    const viewportMetadata = `${window.innerWidth}x${window.innerHeight}`;
    const discoverJobRole = discoverGetStartedPreferences?.value?.jobRole;
    const appPaOwnership = discoverGetStartedPreferences?.value?.appsPas?.workOn;

    let appOwnership;
    let paOwnership;
    if (appPaOwnership === 'all') {
        if (isMultiApp) appOwnership = true;
        paOwnership = true;
    } else if (appPaOwnership) {
        if (isMultiApp) appOwnership = discoverGetStartedPreferences?.value?.appsPas?.selectedApps?.length > 0;
        paOwnership = discoverGetStartedPreferences?.value?.appsPas?.selectedPas?.length > 0;
    } else if (appPaOwnership === 'unknown') {
        if (isMultiApp) appOwnership = null;
        paOwnership = null;
    }

    let fullname;
    if (user.first && user.last) {
        fullname = `${user.first} ${user.last}`;
    } else if (user.first) {
        fullname = user.first;
    } else {
        fullname = username;
    }

    const { isAdmin } = useAuthStore();
    const isViewOnly = useAuthStore().isReadOnly;
    const permissionSummary = `${isAdmin ? 'Admin, ' : ''}${isViewOnly ? 'View Only, ' : ''}${getPermissionString()}`;
    const betaOptIns = [
        ...new Set([
            ...toBetaOptIns(useUserSettingsStore().settingByName(BETA_OPT_INS_SETTING, true)?.value),
            ...toBetaOptIns(useSubscriptionStore().settings?.[BETA_OPT_INS_SETTING]?.value),
        ]),
    ];

    return {
        id: username,
        role: isSuperUser ? USER_ROLES.SUPER : USER_ROLES.USER,
        email: username,
        firstName: user.first,
        lastName: user.last,
        full_name: fullname,
        utm_source: get(user, 'utmMetadata.utmSource'),
        utm_campaign: get(user, 'utmMetadata.utmCampaign'),
        utm_medium: get(user, 'utmMetadata.utmMedium'),
        utm_content: get(user, 'utmMetadata.utmContent'),
        utm_term: get(user, 'utmMetadata.utmTerm'),
        userFlags,
        last_active_account_id: subscription.name,
        window_screen_size_metadata: windowScreenSizeMetadata,
        viewport_metadata: viewportMetadata,
        user_job_role: user.jobRole,
        user_use_case: user.useCase,
        discover_permission: discoverPermission,
        discover_job_role: discoverJobRole,
        app_pa_ownership: appPaOwnership,
        app_ownership: appOwnership,
        pa_ownership: paOwnership,
        permission_summary: permissionSummary,
        active_organization: get(organization, 'id', ''),
        organization_role: formatVisitorOrgRole(),
        [BETA_OPT_INS_SETTING]: betaOptIns,
    };
}

function formatAccountMetadata ({ subscription, applications, productLines }) {
    if (useAuthStore().isSuper) {
        return {
            id: 'pendo-internal',
            impersonationSubName: subscription.name, // leveraged by product-ops and sales-eng in guides
        };
    }

    const subscriptionFlags = useSubscriptionStore().featureFlagList;
    const { isPendoFree } = useSubscriptionStore();
    const { trialDaysLeft, trialEndDateISOString, isInTrial } = getTrialData(subscription);
    const { hadUpfrontTrial } = useSubscriptionStore();
    let subscriptionType = 'fullFeature';

    const freeTrialParams = {
        is_paying: true,
        tags: isPendoFree ? ['Pendo Free'] : ['Pendo Core'],
        is_in_trial: isInTrial,
        trial_end_date: trialEndDateISOString !== 0 ? trialEndDateISOString : null,
    };

    if (isPendoFree || isInTrial) {
        Object.assign(freeTrialParams, {
            is_paying: false,
            monthly_value: 0,
            had_upfront_trial: hadUpfrontTrial,
        });

        subscriptionType = 'pendoFree';
    }

    if (isInTrial) {
        Object.assign(freeTrialParams, {
            number_of_trial_days_remaining: Math.floor(trialDaysLeft),
        });

        subscriptionType = 'freeTrial';
    }

    const { disabled } = subscription.featureFlags;
    if (disabled) {
        subscriptionType = 'disabled';
    }

    const applicationCounts = generateApplicationCounts(applications);

    return {
        id: subscription.name,
        subscriptionFlags,
        beta_subscriptionType: subscriptionType, // The data collected for the subscription type isn’t (and shouldn’t) be used long term.
        feedbackEnabled: get(productLines, 'feedback.enabled'),
        coreEnabled: get(productLines, 'core.enabled'),
        validateEnabled: get(productLines, 'voc.metadata.ideas', false),
        horizonEnabled: get(productLines, 'voc.metadata.feedback', false),
        partnerSubId: get(subscription, 'trainingPartnerSubscriptionId', undefined),
        ...freeTrialParams,
        ...applicationCounts,
    };
}

export function generateApplicationCounts (applications = []) {
    const applicationCounts = applications.reduce((acc, app) => {
        const { platform } = app;
        const platformKey = `${platform}AppCount`;

        if (!acc[platformKey]) acc[platformKey] = 0;

        acc[platformKey]++;

        return acc;
    }, {});

    return applicationCounts;
}

export function sendMarketingPageRegistrationReferralEvent (account, visitor) {
    const eventData = {};
    const pageName = getSanitizedUrlParam('pageName');
    const selectedMAU = parseInt(getSanitizedUrlParam('selectedMAU'));
    const planName = getSanitizedUrlParam('planName');
    const paymentFrequency = getSanitizedUrlParam('paymentFrequency');
    if (!pageName && !selectedMAU && !planName && !paymentFrequency) {
        return;
    }

    if (pageName) {
        eventData.register_pageName = pageName;
    }
    if (selectedMAU) {
        eventData.pricingPage_selectedMAU = selectedMAU;
    }
    if (planName) {
        eventData.pricingPage_planName = planName;
    }
    if (paymentFrequency) {
        eventData.pricingPage_paymentFrequency = paymentFrequency;
    }
    eventData.accountId = account.id;
    eventData.visitorId = visitor.id;
    window.pendo.track('marketingPageRegistrationReferral', eventData);
}
